/**
 * Canonieke Kleda-FAQ: één gedeelde bron voor alle touchpoints.
 *
 * Georganiseerd per fase/context. Elke surface pakt de set die past:
 *   - `algemeen`  → publieke booking-flow, intern kennispaneel, kleda.nl
 *   - `offerte`   → pre-order (publieke booking + offerte-bevestigen):
 *                   de "Offerte/prijs" + "Nieuwe klant" twijfels die het
 *                   grootste vermijdbare belblok vormen
 *   - `ophaal`    → client-app, ophaal-fase
 *   - `reinigen`  → client-app, reinigings-fase
 *   - `aflever`   → client-app, aflever-fase
 *
 * Antwoorden volgen SSOT (brands/kleda/docs/SSOT.md) en de Kleda brand
 * rules: "vanaf €99", nooit prijs per m², niemand hoeft thuis te zijn
 * (kleed klaarleggen), jij/je, geen uitroeptekens, geen em-dash, geen
 * emoji. Canonieke zinnen worden uit KLEDA_CLAIMS gecomponeerd zodat ze
 * nergens uit elkaar lopen. Bij twijfel: pas eerst de SSOT aan, daarna
 * deze bron.
 *
 * De ophaal/reinigen/aflever-sets zijn afgeleid van een analyse van
 * 2.878 inkomende klantberichten (apr-jun 2026).
 */
import { KLEDA_CLAIMS } from './claims'

export interface FaqItem {
  vraag: string
  antwoord: string
}

/**
 * Algemene vragen, merkbreed en niet fase-gebonden. Basis voor de publieke
 * booking-flow, het interne kennispaneel en de kleda.nl-FAQ.
 */
const algemeen: FaqItem[] = [
  {
    vraag: 'Hoe lang duurt het?',
    antwoord: `${KLEDA_CLAIMS.doorlooptijd} Met het pakket Premium Plus gaat het sneller door de prioriteitsservice.`,
  },
  {
    vraag: 'Kunnen jullie alle kleden reinigen?',
    antwoord: 'Ja, alle materialen en types. Wol, zijde, viscose, Perzisch, antiek, alles. We passen de reinigingsmethode aan op het materiaal.',
  },
  {
    vraag: 'Wat kost het?',
    antwoord: 'Vanaf €99. De exacte prijs bepalen we na de voorinspectie in onze wasserij, op basis van de werkelijke afmetingen en het gekozen pakket. Je weet de prijs dus altijd voordat we reinigen.',
  },
  {
    vraag: 'Worden er agressieve chemicaliën gebruikt?',
    antwoord: 'Nee. Onze reiniging is veilig voor kinderen, huisdieren en mensen met allergieën.',
  },
  {
    vraag: 'Moet ik thuis zijn bij het ophalen?',
    antwoord: "Nee, dat hoeft niet. Leg je kleed opgerold klaar op een droge, veilige plek: bij de voordeur, in de schuur of onder een afdak. Geef de plek door via 'Instructies voor de chauffeur'.",
  },
  {
    vraag: 'Wat als een vlek niet weggaat?',
    antwoord: 'We geven altijd een eerlijke verwachting. Sommige vlekken of slijtage zijn niet volledig te verwijderen. Dat vertellen we je bij de voorinspectie.',
  },
  {
    vraag: 'Welk pakket past bij huisdieren?',
    antwoord: `Huisdier Compleet. Dat pakket bevat de ontgeurbehandeling op enzymatische basis: de enzymen breken de geurbron in de vezel af, in plaats van de geur te maskeren met parfum. Premium Plus bevat geen ontgeuren; daar kun je Ontgeuren los bijboeken. ${KLEDA_CLAIMS.geurGrens}`,
  },
  {
    vraag: 'Wanneer moet ik betalen?',
    antwoord: KLEDA_CLAIMS.betalen,
  },
  {
    vraag: 'Kan mijn kleed bij jullie blijven voor opslag?',
    antwoord: KLEDA_CLAIMS.opslag,
  },
]

/**
 * Offerte-fase: precies de pre-order-twijfels van een nieuwe klant
 * (prijs, doorlooptijd, wat-gebeurt-er-na-akkoord, thuis-zijn, betalen).
 * Voor de publieke booking-flow en de offerte-bevestigen app.
 */
const offerte: FaqItem[] = [
  {
    vraag: 'Wat kost het?',
    antwoord: 'Vanaf €99. De exacte prijs bepalen we na de voorinspectie in onze wasserij, op basis van de werkelijke afmetingen en het gekozen pakket. Je weet de prijs dus altijd voordat we reinigen.',
  },
  {
    vraag: 'Hoe lang ben ik mijn kleed kwijt?',
    antwoord: `${KLEDA_CLAIMS.doorlooptijd} Zodra je kleed schoon is, kies je zelf het bezorgmoment.`,
  },
  {
    vraag: 'Wat gebeurt er nadat ik de offerte bevestig?',
    antwoord: 'Je kiest zelf een ophaaldag die jou uitkomt. De dag ervoor ontvang je per e-mail een nauwkeurig tijdsblok van 2 uur. Wij halen je kleed gratis op aan de deur.',
  },
  {
    vraag: 'Moet ik thuis zijn bij het ophalen?',
    antwoord: "Nee, dat hoeft niet. Leg je kleed opgerold klaar op een droge, veilige plek en geef die plek door via 'Instructies voor de chauffeur'.",
  },
  {
    vraag: 'Wanneer en hoe betaal ik?',
    antwoord: `${KLEDA_CLAIMS.betalen} Je betaalt rond de bezorging, niet bij het bevestigen van de offerte.`,
  },
]

/**
 * Boeken, gegevens-stap (booking-flow, vóór submit). Anti-beldrivers uit de
 * belredenen-analyse: "wat gebeurt er nu", "moet ik al betalen" en
 * doorlooptijd zijn de twijfels vlak vóór het versturen van een aanvraag.
 */
const boekenGegevens: FaqItem[] = [
  {
    vraag: 'Wat gebeurt er nadat ik mijn aanvraag verstuur?',
    antwoord: 'Je ontvangt je persoonlijke offerte per e-mail, met de prijs voor jouw kleden. Daarin bevestig je online wanneer het jou uitkomt. Je zit nergens aan vast.',
  },
  {
    vraag: 'Moet ik nu al iets betalen?',
    antwoord: `Nee. ${KLEDA_CLAIMS.betalen} Je betaalt rond de bezorging, niet bij het aanvragen.`,
  },
  {
    vraag: 'Hoe lang ben ik mijn kleed kwijt?',
    antwoord: `${KLEDA_CLAIMS.doorlooptijd} Zodra je kleed schoon is, kies je zelf het bezorgmoment.`,
  },
]

/**
 * Boeken, afspraak-stap (booking-flow, ophaalmoment kiezen). Vangt de
 * "Plannen/verzetten"-belredenen (16% van alle calls) af op het moment
 * dat de vraag ontstaat: tijdsblok, thuis zijn, verzetten, betalen.
 */
const boekenAfspraak: FaqItem[] = [
  {
    vraag: 'Hoe laat komen jullie langs?',
    antwoord: 'Je kiest hier de dag met een ruim tijdsblok. De dag vóór je afspraak ontvang je per e-mail het exacte tijdsblok van 2 uur, zodat je niet de hele dag hoeft te wachten.',
  },
  {
    vraag: 'Moet ik thuis zijn bij het ophalen?',
    antwoord: "Nee, dat hoeft niet. Leg je kleed opgerold klaar op een droge, veilige plek: bij de voordeur, in de schuur of onder een afdak. Geef de plek door via 'Instructies voor de chauffeur'.",
  },
  {
    vraag: 'Kan ik mijn afspraak later nog verzetten?',
    antwoord: 'Ja. Na het boeken kun je je ophaalmoment nog verzetten via je persoonlijke Kleda-omgeving. Tot 48 uur van tevoren is dat gratis.',
  },
  {
    vraag: 'Wanneer en hoe betaal ik?',
    antwoord: `${KLEDA_CLAIMS.betalen} Je betaalt rond de bezorging, niet bij het boeken.`,
  },
]

/**
 * Ophaal-fase (client-app). Antwoorden volgen SSOT §6/§8.
 */
const ophaal: FaqItem[] = [
  {
    vraag: 'Hoe laat komen jullie langs?',
    antwoord: 'Je kiest hier de dag met een ruim tijdsblok. De dag vóór je afspraak ontvang je per e-mail het exacte tijdsblok van 2 uur, zodat je niet de hele dag hoeft te wachten.',
  },
  {
    vraag: 'Moet ik thuis zijn bij het ophalen?',
    antwoord: "Nee, dat hoeft niet. We vragen je wel om het kleed klaar te leggen op een droge, veilige plek: bij de voordeur, in de schuur of onder een afdak. Geef de plek door via 'Instructies voor de chauffeur'.",
  },
  {
    vraag: 'Hoe leg ik mijn kleed klaar? Moet er een touwtje omheen?',
    antwoord: 'Opgerold klaarleggen is genoeg. Een touwtje of tape hoeft niet, zolang het kleed opgerold blijft. De chauffeur komt alleen: gaat het om een groot, zwaar kleed, zorg dan dat er iemand kan helpen om het in de bus te leggen, tenzij je een bijrijder hebt bijgeboekt.',
  },
  {
    vraag: 'Hoe lang ben ik mijn kleed kwijt?',
    antwoord: 'Gemiddeld 5 tot 10 werkdagen tussen ophalen en thuisbezorgen. Zodra je kleed klaar is, kies je zelf het bezorgmoment.',
  },
  {
    vraag: 'Kan ik mijn afspraak later nog verzetten?',
    antwoord: 'Ja. Na het bevestigen kun je je ophaalmoment hier verzetten. Tot 48 uur van tevoren is dat gratis.',
  },
  {
    vraag: 'Kan ik nog een kleed toevoegen?',
    antwoord: "Ja, tot het moment van ophalen. Voeg het toe via 'Jouw bestelling'. Je ziet direct de prijs en eventuele extra korting.",
  },
  {
    vraag: 'Wat als de maten niet precies kloppen?',
    antwoord: "Geen probleem. Bij de voorinspectie in onze wasserij meten we elk kleed exact na. Je kunt de maten ook alvast zelf aanpassen via 'Jouw bestelling'.",
  },
]

/**
 * Reinigings-fase (client-app, Opgehaald t/m Aan het reinigen).
 */
const reinigen: FaqItem[] = [
  {
    vraag: 'Hoe lang duurt het?',
    antwoord: 'Gemiddeld 5 tot 10 werkdagen tussen ophalen en thuisbezorgen.',
  },
  {
    vraag: 'Hoe weet ik wanneer mijn kleed klaar is?',
    antwoord: 'Je ontvangt een e-mail zodra je kleed schoon is. Daarin kies je zelf het bezorgmoment.',
  },
  {
    vraag: 'Wat is de voorinspectie?',
    antwoord: 'Voordat we wassen meten we je kleed exact na en bekijken we vlekken, randen en bijzonderheden. Je ontvangt hiervan bericht.',
  },
]

/**
 * Aflever-fase (client-app). Betaalvragen = grootste categorie uit de
 * inbox-analyse; antwoorden volgen SSOT §6B/§8/§13.
 */
const aflever: FaqItem[] = [
  {
    vraag: 'Hoe laat komen jullie langs?',
    antwoord: 'Je kiest hier de dag met een ruim tijdsblok. De dag vóór je afspraak ontvang je per e-mail het exacte tijdsblok van 2 uur, zodat je niet de hele dag hoeft te wachten.',
  },
  {
    vraag: 'Moet ik thuis zijn bij de bezorging?',
    antwoord: "Dat hoeft niet. Betaal dan wel vooraf via iDEAL en geef via 'Instructies voor de bezorging' een veilige plek door waar we je kleed mogen neerleggen. Betaal je liever bij aflevering (pin of contant gepast)? Dan hebben we je even aan de deur nodig.",
  },
  {
    vraag: 'Hoe kan ik betalen?',
    antwoord: `${KLEDA_CLAIMS.betalen} Vooraf betalen kan via de betaalknop bij 'Jouw bestelling'.`,
  },
  {
    vraag: 'Ik heb al betaald, maar zie "Betaling openstaand"?',
    antwoord: 'Net betaald? De verwerking duurt soms een paar minuten, ververs de pagina dan even. Blijft het staan, bel ons dan gerust.',
  },
  {
    vraag: 'Kan ik het aflevermoment later nog verzetten?',
    antwoord: 'Ja. Na het bevestigen kun je je aflevermoment hier verzetten. Tot 48 uur van tevoren is dat gratis.',
  },
  {
    vraag: 'Kan het kleed nog even bij jullie blijven?',
    antwoord: `Natuurlijk, kies gewoon een later moment. ${KLEDA_CLAIMS.opslag} Bij elk moment hierboven zie je of er opslagkosten gelden.`,
  },
]

/** Per-fase FAQ-sets. Sleutel = context; waarde = FaqItem[]. */
export const KLEDA_FAQ = {
  algemeen,
  offerte,
  boekenGegevens,
  boekenAfspraak,
  ophaal,
  reinigen,
  aflever,
} as const
